import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField,
  Alert, Chip, Divider, IconButton, Tooltip,
  InputAdornment, Switch, Snackbar, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Cloud, Refresh, Save, CheckCircle, Error as ErrorIcon,
  Visibility, VisibilityOff, Speed, Thermostat, WaterDrop,
  Lock, PowerSettingsNew,
  Shield, Timeline, SyncProblem, SignalWifiStatusbar4Bar,
  DeleteSweep, Api, AccessTime, TrendingUp,
} from '@mui/icons-material';
import { weatherAPI } from 'services/api';

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

// ═══════════════════════════════════════════════════════════════
// KPI CARD
// ═══════════════════════════════════════════════════════════════

const KPICard = ({ icon, label, value, color, sub }) => (
  <Card sx={{
    borderRadius: 2, border: '1px solid #e2e8f0',
    transition: 'all 0.2s ease',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${color}20` },
  }}>
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box className="flex items-center justify-between">
        <Box>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color, lineHeight: 1.2, mt: 0.3 }}>
            {value}
          </Typography>
          {sub && <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', mt: 0.2 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}10` }}>
          {React.cloneElement(icon, { sx: { fontSize: 20, color } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ═══════════════════════════════════════════════════════════════
// SECTION HELPER
// ═══════════════════════════════════════════════════════════════

const Section = ({ icon, title, color, children, action }) => (
  <Box>
    <Box className="flex items-center justify-between mb-1.5">
      <Box className="flex items-center gap-1.5">
        {React.cloneElement(icon, { sx: { fontSize: 16, color } })}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

const InfoRow = ({ label, value }) => (
  <Box className="flex items-center justify-between py-0.5">
    <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', minWidth: 80 }}>{label}</Typography>
    {typeof value === 'string' || typeof value === 'number' ? (
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e293b', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
        {value || '—'}
      </Typography>
    ) : value}
  </Box>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const WeatherSettings = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [clearing, setClearing] = useState(false);

  const [featureStatus, setFeatureStatus] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testTimestamp, setTestTimestamp] = useState(null);

  // API key form
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // Error log (client-side tracking of API errors during this session)
  const [errorLog, setErrorLog] = useState([]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Load Data ──
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);

      const [featureRes, cacheRes] = await Promise.all([
        weatherAPI.admin.getFeatureStatus(),
        weatherAPI.admin.getCacheStats(),
      ]);

      setFeatureStatus(featureRes.data);
      setCacheStats(cacheRes.data);
    } catch (err) {
      console.error('Error loading weather settings:', err);
      setSnackbar({ open: true, message: 'Failed to load weather settings', severity: 'error' });
      addError('Load Settings', err.message || 'Failed to load settings', '—');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Error logger ──
  const addError = (action, message, ref) => {
    setErrorLog(prev => [
      { timestamp: new Date().toISOString(), action, message: message || 'Unknown error', ref: ref || '—' },
      ...prev.slice(0, 19), // Keep last 20
    ]);
  };

  // ── Save API Key ──
  const handleSaveApiKey = async () => {
    if (!apiKey.trim()) {
      setSnackbar({ open: true, message: 'Please enter an API key', severity: 'error' });
      return;
    }
    if (apiKey.length < 20) {
      setSnackbar({ open: true, message: 'API key seems too short. OpenWeather keys are typically 32 characters.', severity: 'warning' });
      return;
    }
    try {
      setSaving(true);
      await weatherAPI.admin.setApiKey(apiKey.trim());
      setSnackbar({ open: true, message: 'API key saved successfully!', severity: 'success' });
      setApiKey('');
      await loadData(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save API key';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      addError('Save API Key', msg, '—');
    } finally {
      setSaving(false);
    }
  };

  // ── Test Connection ──
  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      // Test with Bhubaneswar, Odisha
      const response = await weatherAPI.admin.testConnection(20.2961, 85.8245);
      setTestResult(response.data);
      setTestTimestamp(new Date().toISOString());
      if (!response.data.success) {
        addError('Test Connection', response.data.message, 'Bhubaneswar (20.29, 85.82)');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Connection test failed';
      setTestResult({ success: false, message: msg });
      setTestTimestamp(new Date().toISOString());
      addError('Test Connection', msg, 'Bhubaneswar (20.29, 85.82)');
    } finally {
      setTesting(false);
    }
  };

  // ── Toggle Feature ──
  const handleToggleFeature = async () => {
    try {
      setToggling(true);
      const newState = !featureStatus?.enabled;
      await weatherAPI.admin.toggleFeature(newState);
      setSnackbar({ open: true, message: `Weather feature ${newState ? 'enabled' : 'disabled'} successfully`, severity: 'success' });
      await loadData(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to toggle feature';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      addError('Toggle Feature', msg, '—');
    } finally {
      setToggling(false);
    }
  };

  // ── Clear Cache ──
  const handleClearCache = async () => {
    try {
      setClearing(true);
      await weatherAPI.admin.clearCache();
      setSnackbar({ open: true, message: 'Cache cleared successfully', severity: 'success' });
      await loadData(true);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to clear cache', severity: 'error' });
      addError('Clear Cache', 'Failed to clear weather cache', '—');
    } finally {
      setClearing(false);
    }
  };

  // ── Derived values ──
  const isActive = featureStatus?.can_use || false;
  const isConfigured = featureStatus?.api_configured || false;
  const isEnabled = featureStatus?.enabled || false;

  // ── Loading ──
  if (loading) {
    return (
      <Box className="flex flex-col items-center justify-center" sx={{ height: '60vh' }}>
        <Box sx={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #0ea5e9', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
        <Typography sx={{ color: '#64748b', mt: 2, fontWeight: 500 }}>Loading integration settings…</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 1.5, md: 2.5 } }}>
      {refreshing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      {/* ══════════ HEADER ══════════ */}
      <Box className="flex items-center justify-between mb-3">
        <Box className="flex items-center gap-2">
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Cloud sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
              Weather Integration Control
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
              OpenWeather API — Configuration & Monitoring
            </Typography>
          </Box>
        </Box>
        <Box className="flex items-center gap-2">
          {/* 7️⃣ Enable/Disable toggle */}
          <Box className="flex items-center gap-1.5" sx={{ px: 1.5, py: 0.5, borderRadius: 2, bgcolor: isEnabled ? '#dcfce7' : '#fef2f2', border: `1px solid ${isEnabled ? '#22c55e' : '#fca5a5'}` }}>
            <PowerSettingsNew sx={{ fontSize: 16, color: isEnabled ? '#16a34a' : '#dc2626' }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: isEnabled ? '#16a34a' : '#dc2626' }}>
              {toggling ? 'Toggling…' : isEnabled ? 'Enabled' : 'Disabled'}
            </Typography>
            <Switch
              size="small" checked={isEnabled}
              onChange={handleToggleFeature}
              disabled={toggling || !isConfigured}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' },
              }}
            />
          </Box>
          <Tooltip title="Refresh"><IconButton size="small" onClick={() => loadData(true)} disabled={refreshing}><Refresh sx={{ fontSize: 20 }} /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* ══════════ 2. INTEGRATION STATUS + API USAGE KPIs ══════════ */}
      <Box className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-3">
        {/* Integration Status */}
        <KPICard icon={<SignalWifiStatusbar4Bar />} label="Status" color={isActive ? '#16a34a' : '#dc2626'}
          value={isActive ? 'Active' : 'Inactive'}
          sub={featureStatus?.status_message?.substring(0, 40) || ''} />
        <KPICard icon={<AccessTime />} label="Last API Call" color="#7c3aed"
          value={testTimestamp ? fmtTime(testTimestamp) : '—'}
          sub={testResult?.success ? 'Success' : testResult ? 'Failed' : 'No test yet'} />
        <KPICard icon={<TrendingUp />} label="Calls Today (24h)" color="#2563eb"
          value={cacheStats?.fetches_24h ?? '—'} sub="Cache fetches" />
        <KPICard icon={<SyncProblem />} label="Errors (Session)" color={errorLog.length > 0 ? '#dc2626' : '#16a34a'}
          value={errorLog.length} sub="This session" />
        {/* 3️⃣ API Usage Metrics */}
        <KPICard icon={<Api />} label="Total Snapshots" color="#0891b2"
          value={cacheStats?.total_snapshots ?? '—'} sub="All time" />
        <KPICard icon={<Speed />} label="Cache Health" color={
          cacheStats ? (cacheStats.valid_cache > cacheStats.expired_cache ? '#16a34a' : '#d97706') : '#64748b'}
          value={cacheStats ? `${cacheStats.valid_cache}/${cacheStats.total_snapshots}` : '—'}
          sub={`${cacheStats?.expired_cache || 0} expired`} />
      </Box>

      {/* ══════════ TWO-COLUMN LAYOUT ══════════ */}
      <Box className="flex gap-2.5" sx={{ minHeight: 420 }}>

        {/* ── LEFT COLUMN ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* 2️⃣ Integration Status Card */}
          <Card sx={{
            borderRadius: 2, overflow: 'hidden',
            border: `2px solid ${isActive ? '#22c55e' : '#fca5a5'}`,
          }}>
            <Box sx={{ px: 2, py: 1.2, bgcolor: isActive ? '#f0fdf4' : '#fef2f2' }}>
              <Box className="flex items-center justify-between">
                <Box className="flex items-center gap-2">
                  <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: isActive ? '#dcfce7' : '#fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isActive ? <CheckCircle sx={{ fontSize: 18, color: '#16a34a' }} /> : <ErrorIcon sx={{ fontSize: 18, color: '#dc2626' }} />}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: isActive ? '#16a34a' : '#dc2626' }}>
                      {isActive ? 'Integration Active' : 'Integration Inactive'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>
                      {featureStatus?.status_message || 'Loading…'}
                    </Typography>
                  </Box>
                </Box>
                <Box className="flex items-center gap-1.5">
                  <Chip label={isConfigured ? 'API Configured' : 'API Missing'} size="small"
                    sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: isConfigured ? '#dcfce7' : '#fef3c7', color: isConfigured ? '#16a34a' : '#d97706' }} />
                  <Chip label={isEnabled ? 'Enabled' : 'Disabled'} size="small"
                    sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: isEnabled ? '#dbeafe' : '#f1f5f9', color: isEnabled ? '#2563eb' : '#64748b' }} />
                </Box>
              </Box>
            </Box>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box className="grid grid-cols-2 gap-x-4">
                <InfoRow label="API Key" value={
                  isConfigured ? (
                    <Box className="flex items-center gap-1">
                      <Lock sx={{ fontSize: 12, color: '#16a34a' }} />
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, fontFamily: 'monospace', color: '#1e293b' }}>
                        {featureStatus?.api_key_masked || '••••••••'}
                      </Typography>
                    </Box>
                  ) : (
                    <Chip label="Not Set" size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: '#fef2f2', color: '#dc2626' }} />
                  )
                } />
                <InfoRow label="Updated" value={featureStatus?.api_key_updated_at ? fmtDateTime(featureStatus.api_key_updated_at) : '—'} />
                <InfoRow label="Valid Cache" value={cacheStats?.valid_cache ?? '—'} />
                <InfoRow label="Expired Cache" value={cacheStats?.expired_cache ?? '—'} />
                <InfoRow label="Fetches (24h)" value={cacheStats?.fetches_24h ?? '—'} />
                <InfoRow label="Total Snapshots" value={cacheStats?.total_snapshots ?? '—'} />
              </Box>
            </CardContent>
          </Card>

          {/* 4️⃣ Test Connection */}
          <Section icon={<Speed />} title="Test Connection" color="#7c3aed"
            action={
              <Button size="small" variant={testResult?.success ? 'outlined' : 'contained'}
                startIcon={testing ? null : <Refresh />}
                onClick={handleTestConnection}
                disabled={testing || !isConfigured}
                sx={{
                  textTransform: 'none', fontWeight: 700, borderRadius: 2, fontSize: '0.68rem',
                  ...(testResult?.success
                    ? { borderColor: '#16a34a', color: '#16a34a' }
                    : { bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }),
                }}>
                {testing ? 'Testing…' : 'Test Now'}
              </Button>
            }>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {!isConfigured && (
                  <Alert severity="warning" sx={{ borderRadius: 2, mb: 1, '& .MuiAlert-message': { fontSize: '0.72rem' } }}>
                    Configure API key first to test connection.
                  </Alert>
                )}

                {testResult ? (
                  <Box>
                    <Box className="flex items-center gap-2 mb-1.5">
                      {testResult.success
                        ? <CheckCircle sx={{ fontSize: 20, color: '#16a34a' }} />
                        : <ErrorIcon sx={{ fontSize: 20, color: '#dc2626' }} />}
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: testResult.success ? '#16a34a' : '#dc2626' }}>
                        {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                      </Typography>
                      {testTimestamp && (
                        <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', ml: 'auto' }}>
                          {fmtDateTime(testTimestamp)}
                        </Typography>
                      )}
                    </Box>

                    {testResult.success && testResult.weather ? (
                      <Box className="grid grid-cols-3 gap-2">
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#fef3c7', borderRadius: 2 }}>
                          <Thermostat sx={{ fontSize: 18, color: '#d97706' }} />
                          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                            {Math.round(testResult.weather.temperature)}°C
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>Temperature</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#dbeafe', borderRadius: 2 }}>
                          <WaterDrop sx={{ fontSize: 18, color: '#2563eb' }} />
                          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                            {testResult.weather.humidity}%
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>Humidity</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                          <Cloud sx={{ fontSize: 18, color: '#64748b' }} />
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                            {testResult.weather.weather_main}
                          </Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>Weather</Typography>
                        </Box>
                      </Box>
                    ) : !testResult.success && (
                      <Typography sx={{ fontSize: '0.75rem', color: '#dc2626' }}>{testResult.message}</Typography>
                    )}

                    {testResult.success && (
                      <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', mt: 1, textAlign: 'center' }}>
                        Test location: Bhubaneswar, Odisha (20.30, 85.82)
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box className="text-center" sx={{ py: 2 }}>
                    <Speed sx={{ fontSize: 32, color: '#cbd5e1', mb: 0.5 }} />
                    <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                      Click "Test Now" to verify API connectivity
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Section>

          {/* 5️⃣ Error Log Table */}
          <Section icon={<SyncProblem />} title="Error Log (Session)" color="#dc2626"
            action={errorLog.length > 0 && (
              <Button size="small" onClick={() => setErrorLog([])}
                sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 600, fontSize: '0.62rem' }}>
                Clear
              </Button>
            )}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              {errorLog.length === 0 ? (
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, textAlign: 'center' }}>
                  <CheckCircle sx={{ fontSize: 28, color: '#22c55e', opacity: 0.4, mb: 0.5 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8' }}>
                    No errors recorded this session
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#cbd5e1', mt: 0.3 }}>
                    Errors from API calls will appear here in real time.
                  </Typography>
                </CardContent>
              ) : (
                <TableContainer sx={{ maxHeight: 220 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.5 }}>Time</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.5 }}>Error</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.5 }}>Reference</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errorLog.map((err, i) => (
                        <TableRow key={i} sx={{ bgcolor: i === 0 ? '#fef2f2' : 'transparent' }}>
                          <TableCell sx={{ fontSize: '0.62rem', fontFamily: 'monospace', color: '#64748b', py: 0.5, whiteSpace: 'nowrap' }}>
                            {fmtTime(err.timestamp)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.65rem', color: '#dc2626', py: 0.5, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            [{err.action}] {err.message}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.62rem', color: '#94a3b8', py: 0.5 }}>
                            {err.ref}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Section>
        </Box>

        {/* ── RIGHT COLUMN ── */}
        <Box sx={{ flex: '0 0 340px', display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* API Key Configuration */}
          <Section icon={<Lock />} title="API Configuration" color="#0f172a">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Current Status */}
                <Box sx={{ p: 1.5, bgcolor: isConfigured ? '#f0fdf4' : '#fef2f2', borderRadius: 2, mb: 2 }}>
                  <Box className="flex items-center gap-1.5">
                    {isConfigured ? <CheckCircle sx={{ fontSize: 16, color: '#16a34a' }} /> : <ErrorIcon sx={{ fontSize: 16, color: '#dc2626' }} />}
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: isConfigured ? '#16a34a' : '#dc2626' }}>
                      {isConfigured ? 'API Key Configured (Encrypted)' : 'API Key Not Configured'}
                    </Typography>
                  </Box>
                  {isConfigured && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography sx={{ fontSize: '0.65rem', fontFamily: 'monospace', color: '#64748b' }}>
                        Key: {featureStatus?.api_key_masked || '••••••••'}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Input */}
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#475569', mb: 0.5 }}>
                  {isConfigured ? 'Update' : 'Set'} OpenWeather API Key
                </Typography>
                <TextField
                  fullWidth size="small"
                  type={showApiKey ? 'text' : 'password'}
                  placeholder="Enter your OpenWeather API key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc', fontSize: '0.78rem' } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end" size="small">
                          {showApiKey ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button fullWidth variant="contained" size="small"
                  startIcon={saving ? null : <Save />}
                  onClick={handleSaveApiKey}
                  disabled={saving || !apiKey.trim()}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 0.8, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}>
                  {saving ? 'Saving…' : 'Save API Key'}
                </Button>

                <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.65rem' } }}>
                  Get your free API key from{' '}
                  <a href="https://openweathermap.org/api" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontWeight: 700 }}>
                    openweathermap.org
                  </a>
                </Alert>
              </CardContent>
            </Card>
          </Section>

          {/* Cache Management */}
          <Section icon={<DeleteSweep />} title="Cache Management" color="#d97706"
            action={
              <Button size="small" variant="outlined" color="warning"
                onClick={handleClearCache} disabled={clearing}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: '0.62rem' }}>
                {clearing ? 'Clearing…' : 'Clear All'}
              </Button>
            }>
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box className="grid grid-cols-2 gap-2">
                  <Box sx={{ p: 1, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#2563eb' }}>
                      {cacheStats?.total_snapshots ?? '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>Total Snapshots</Typography>
                  </Box>
                  <Box sx={{ p: 1, bgcolor: '#f0fdf4', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#16a34a' }}>
                      {cacheStats?.valid_cache ?? '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>Valid Cache</Typography>
                  </Box>
                  <Box sx={{ p: 1, bgcolor: '#fef3c7', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#d97706' }}>
                      {cacheStats?.expired_cache ?? '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>Expired</Typography>
                  </Box>
                  <Box sx={{ p: 1, bgcolor: '#dbeafe', borderRadius: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#2563eb' }}>
                      {cacheStats?.fetches_24h ?? '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>Last 24h</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Section>

          {/* 6️⃣ Audit Trail */}
          <Section icon={<Timeline />} title="Audit Trail" color="#64748b">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box className="flex items-start gap-2 mb-2">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2563eb', mt: 0.5, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>API Key Last Updated</Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                      {featureStatus?.api_key_updated_at ? fmtDateTime(featureStatus.api_key_updated_at) : 'Never configured'}
                    </Typography>
                  </Box>
                </Box>
                <Box className="flex items-start gap-2 mb-2">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isEnabled ? '#16a34a' : '#dc2626', mt: 0.5, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>
                      Feature {isEnabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                      Current state — toggle via header switch
                    </Typography>
                  </Box>
                </Box>
                {testTimestamp && (
                  <Box className="flex items-start gap-2 mb-2">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: testResult?.success ? '#16a34a' : '#dc2626', mt: 0.5, flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#1e293b' }}>
                        Connection Test — {testResult?.success ? 'Passed' : 'Failed'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                        {fmtDateTime(testTimestamp)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box className="flex items-center gap-1">
                  <Shield sx={{ fontSize: 12, color: '#94a3b8' }} />
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    API keys are stored encrypted and never exposed to the frontend.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Section>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WeatherSettings;
